class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.INFINITE_LOOPS_COUNT = 2;
    this.LAYOUT_DELAY_MS = 40;
    this.MAX_SLIDES_MULTIPLIER = 10;

    this._activeImageIndex = 0;
    this.images = [];
    this.isHovered = false;
    this.isMobile = false;
    this._isInitializing = false;

    this.pageScrollHandler = null;
    this._resizeHandler = null;
    this._mouseEnterHandler = null;
    this._mouseLeaveHandler = null;
    this._mobileScrollHandler = null;
    this._scrollTimeout = null;

    this._mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this._cachedElements = {};
  }

  isMobileView() {
    return this._mobileMediaQuery.matches;
  }

  connectedCallback() {
    this.init();

    let resizeTimeout;
    this._resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        const isNowMobile = this.isMobileView();

        if (wasMobile !== isNowMobile) {
          this.isMobile = isNowMobile;
          this.init();
        }
      }, 150);
    };

    window.addEventListener('resize', this._resizeHandler, { passive: true });
  }

  disconnectedCallback() {
    if (this.pageScrollHandler) {
      window.removeEventListener('scroll', this.pageScrollHandler);
      this.pageScrollHandler = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    if (this._mouseEnterHandler) {
      this.removeEventListener('mouseenter', this._mouseEnterHandler);
      this._mouseEnterHandler = null;
    }
    if (this._mouseLeaveHandler) {
      this.removeEventListener('mouseleave', this._mouseLeaveHandler);
      this._mouseLeaveHandler = null;
    }

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');
    if (scrollWrapper && this._mobileScrollHandler) {
      scrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = null;
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    this.images = [];
    this._cachedElements = {};
  }

  init() {
    if (this._mouseEnterHandler) {
      this.removeEventListener('mouseenter', this._mouseEnterHandler);
      this._mouseEnterHandler = null;
    }
    if (this._mouseLeaveHandler) {
      this.removeEventListener('mouseleave', this._mouseLeaveHandler);
      this._mouseLeaveHandler = null;
    }

    const oldScrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');
    if (oldScrollWrapper && this._mobileScrollHandler) {
      oldScrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    this.images = Array.from(this.querySelectorAll('img.ProductItem__Image'));

    if (this.images.length === 0) {
      this.images = Array.from(this.querySelectorAll('img[data-media-id]'));
    }

    this._activeImageIndex = 0;
    this.isMobile = this.isMobileView();
    this._cachedElements = {};

    if (this.isMobile) {
      this.setupMobile();
    } else {
      this.setupDesktop();
    }
  }

  setupDesktop() {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length > 1) {
      this._mouseEnterHandler = this.handleMouseEnter.bind(this);
      this._mouseLeaveHandler = this.handleMouseLeave.bind(this);

      this.addEventListener('mouseenter', this._mouseEnterHandler);
      this.addEventListener('mouseleave', this._mouseLeaveHandler);
      this.createArrows();
    }
  }

  setupMobile() {
    if (this.images.length <= 1) return;

    const carouselArrowsEnabled =
      this.getAttribute('data-carousel-arrows-enabled') !== 'false';

    if (!carouselArrowsEnabled) return;

    const imageContainer = this.querySelector('.ProductItem__ImageContainer');
    const aspectRatio =
      imageContainer?.querySelector('.AspectRatio') || imageContainer;

    if (!aspectRatio) return;

    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );
    const naturalOrderImages = [...carouselImages];
    const { orderedImages, rolloverImageNumber } =
      this.buildImageSequence(carouselImages);
    const scrollWrapper = this.createScrollWrapperInfinite(
      naturalOrderImages,
      orderedImages,
    );

    this.rolloverImageNumber = rolloverImageNumber;
    this.setupSequenceCounts(carouselImages.length, orderedImages.length);

    const oldImages = Array.from(this.images);
    const mainImage = oldImages.find(img =>
      !img.classList.contains('ProductItem__Image--alternate') &&
      !img.classList.contains('ProductItem__Image--carousel')
    ) || oldImages[0];

    oldImages.forEach(img => {
      if (img !== mainImage) {
        img.style.opacity = '0';
      }
    });

    if (mainImage) {
      mainImage.style.position = 'absolute';
      mainImage.style.top = '0';
      mainImage.style.left = '0';
      mainImage.style.width = '100%';
      mainImage.style.height = '100%';
      mainImage.style.objectFit = 'cover';
      mainImage.style.zIndex = '10';
      mainImage.style.pointerEvents = 'none';
    }

    scrollWrapper.style.opacity = '0';
    aspectRatio.appendChild(scrollWrapper);
    this.images = Array.from(scrollWrapper.querySelectorAll('img'));

    if (this.prependedSlides > 0) {
      scrollWrapper.style.scrollBehavior = 'auto';
      const tempWidth = aspectRatio.offsetWidth || this.offsetWidth || 300;
      scrollWrapper.scrollLeft = this.prependedSlides * tempWidth;
    }

    this.setInitialScrollPosition(scrollWrapper);
    this.createProgressBarAfterImageWrapper();

    const showCarousel = () => {
      scrollWrapper.style.opacity = '1';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mainImage) {
            mainImage.style.display = 'none';
          }

          oldImages.forEach(img => {
            if (img !== mainImage) {
              img.style.display = 'none';
            }
          });

          setTimeout(() => {
            oldImages.forEach(img => img.remove());
          }, 100);
        });
      });
    };

    if (!this.prependedSlides || this.prependedSlides <= 0) {
      requestAnimationFrame(showCarousel);
    } else {
      this._showCarouselCallback = showCarousel;
    }

    this._mobileScrollHandler = this.handleMobileScroll.bind(this);
    scrollWrapper.addEventListener('scroll', this._mobileScrollHandler);
    this.setupPageScrollReset();
  }

  fixImageUrls(img) {
    ['src', 'srcset'].forEach((attr) => {
      const value = img.getAttribute(attr);

      if (value && value.includes('{width}')) {
        img.setAttribute(attr, value.replace(/{width}x/g, '600x'));
      }
    });
  }

  setInitialScrollPosition(scrollWrapper) {
    const progressFill = this.querySelector('.ProductItem__ProgressFill');

    if (progressFill) {
      progressFill.style.transform = 'translateX(0)';
    }

    if (this.prependedSlides > 0) {
      this._isInitializing = true;
      scrollWrapper.style.opacity = '0';

      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollWrapper.style.scrollBehavior = 'auto';

          const container = scrollWrapper.parentElement;
          let slideWidth = Math.round(container ? container.offsetWidth : 0);

          if (slideWidth === 0) {
            const productItem = this.querySelector('.ProductItem__ImageContainer');
            slideWidth = productItem ? Math.round(productItem.offsetWidth) : Math.round(this.offsetWidth);
          }

          scrollWrapper
            .querySelectorAll('.ProductItem__ImageSlide')
            .forEach((slide) => {
              slide.style.minWidth = (slideWidth + 1) + 'px';
              slide.style.width = (slideWidth + 1) + 'px';
            });

          scrollWrapper.scrollLeft = this.prependedSlides * slideWidth;
          this._activeImageIndex = this.prependedSlides;

          if (progressFill) {
            progressFill.style.transform = 'translateX(0)';
          }

          requestAnimationFrame(() => {
            scrollWrapper.style.scrollBehavior = '';
            this._isInitializing = false;

            if (this._showCarouselCallback) {
              this._showCarouselCallback();
              this._showCarouselCallback = null;
            } else {
              scrollWrapper.style.opacity = '1';
            }
          });
        }, this.LAYOUT_DELAY_MS);
      });
    }
  }

  createSlideElement(img) {
    const slide = document.createElement('div');
    slide.className = 'ProductItem__ImageSlide';
    slide.style.scrollSnapAlign = 'start';
    slide.style.scrollSnapStop = 'always';

    const clonedImg = img.cloneNode(true);
    clonedImg.className = 'ProductItem__Image';
    clonedImg.loading = 'lazy';

    this.fixImageUrls(clonedImg);
    slide.appendChild(clonedImg);

    return slide;
  }

  buildImageSequence(carouselImages) {
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );
    const mainImage = carouselImages[0];
    const orderedImages = [];

    let rolloverImgIndex = -1;
    let rolloverImageNumber = 2;

    if (alternateImage) {
      const alternateUrl = alternateImage.getAttribute('src');

      rolloverImgIndex = carouselImages.findIndex(
        (img) =>
          img.getAttribute('src') === alternateUrl && img !== mainImage,
      );
      rolloverImageNumber = rolloverImgIndex + 1;
    }

    if (mainImage) {
      orderedImages.push(mainImage);
    }

    if (rolloverImgIndex !== -1) {
      orderedImages.push(carouselImages[rolloverImgIndex]);

      for (let i = rolloverImgIndex + 1; i < carouselImages.length; i++) {
        orderedImages.push(carouselImages[i]);
      }
    } else {
      for (let i = 1; i < carouselImages.length; i++) {
        orderedImages.push(carouselImages[i]);
      }
    }

    return { orderedImages, rolloverImageNumber };
  }

  createScrollWrapperInfinite(naturalOrderImages, orderedImages) {
    const scrollWrapper = document.createElement('div');
    scrollWrapper.className = 'ProductItem__ScrollWrapper';
    scrollWrapper.style.scrollSnapType = 'x mandatory';

    this.appendImagesToWrapper(
      scrollWrapper,
      naturalOrderImages,
      this.INFINITE_LOOPS_COUNT,
    );

    const firstVisibleSlideIndex = this.INFINITE_LOOPS_COUNT * naturalOrderImages.length;

    this.appendImagesToWrapper(scrollWrapper, orderedImages, 1, firstVisibleSlideIndex);

    this.appendImagesToWrapper(
      scrollWrapper,
      naturalOrderImages,
      this.INFINITE_LOOPS_COUNT,
    );

    this.prependedSlides = firstVisibleSlideIndex;

    return scrollWrapper;
  }

  appendImagesToWrapper(scrollWrapper, images, loopCount, firstVisibleSlideIndex = null) {
    for (let loop = 0; loop < loopCount; loop++) {
      images.forEach((img, imgIndex) => {
        const slide = this.createSlideElement(img);

        if (firstVisibleSlideIndex !== null &&
            scrollWrapper.children.length === firstVisibleSlideIndex &&
            loop === 0 && imgIndex === 0) {
          slide.classList.add('ProductItem__ImageSlide--first');
        }

        scrollWrapper.appendChild(slide);
      });
    }
  }

  setupSequenceCounts(carouselImagesCount, orderedImagesLength) {
    let firstLoopCount = 1;
    const rolloverImgIndex = this.rolloverImageNumber - 1;

    if (rolloverImgIndex !== -1) {
      firstLoopCount++;
      firstLoopCount += carouselImagesCount - rolloverImgIndex - 1;
    } else {
      firstLoopCount += carouselImagesCount - 1;
    }

    this.firstLoopImageCount = firstLoopCount;
    this.originalImageCount = carouselImagesCount;
    this.originalMobileImageCount = orderedImagesLength;
  }

  buildUrlSequence(validUrls, rolloverImageNumber) {
    const orderedUrls = [];
    let rolloverIndex = null;

    if (
      typeof rolloverImageNumber === 'number' &&
      rolloverImageNumber > 1 &&
      rolloverImageNumber <= validUrls.length
    ) {
      rolloverIndex = rolloverImageNumber - 1;
    } else if (validUrls.length > 1) {
      rolloverIndex = 1;
    }

    orderedUrls.push(validUrls[0]);

    if (
      rolloverIndex !== null &&
      rolloverIndex < validUrls.length &&
      rolloverIndex !== 0
    ) {
      orderedUrls.push(validUrls[rolloverIndex]);
    }

    if (rolloverIndex !== null) {
      for (let i = rolloverIndex + 1; i < validUrls.length; i++) {
        orderedUrls.push(validUrls[i]);
      }
    } else {
      for (let i = 1; i < validUrls.length; i++) {
        orderedUrls.push(validUrls[i]);
      }
    }

    return {
      orderedUrls,
      rolloverImageNumber: rolloverIndex !== null ? rolloverIndex + 1 : 2,
    };
  }

  createScrollWrapperFromUrls(naturalOrderUrls, orderedUrls) {
    const scrollWrapper = document.createElement('div');
    scrollWrapper.className = 'ProductItem__ScrollWrapper';
    scrollWrapper.style.scrollSnapType = 'x mandatory';

    this.appendUrlsToWrapper(scrollWrapper, naturalOrderUrls, this.INFINITE_LOOPS_COUNT);

    const firstVisibleSlideIndex = this.INFINITE_LOOPS_COUNT * naturalOrderUrls.length;
    this.appendUrlsToWrapper(scrollWrapper, orderedUrls, 1, firstVisibleSlideIndex);

    this.appendUrlsToWrapper(scrollWrapper, naturalOrderUrls, this.INFINITE_LOOPS_COUNT);

    this.prependedSlides = firstVisibleSlideIndex;
    return scrollWrapper;
  }

  appendUrlsToWrapper(scrollWrapper, urls, loopCount, firstVisibleSlideIndex = null) {
    for (let loop = 0; loop < loopCount; loop++) {
      urls.forEach((url, urlIndex) => {
        const slide = this.createSlideFromUrl(url);

        if (firstVisibleSlideIndex !== null &&
            scrollWrapper.children.length === firstVisibleSlideIndex &&
            loop === 0 && urlIndex === 0) {
          slide.classList.add('ProductItem__ImageSlide--first');
        }

        scrollWrapper.appendChild(slide);
      });
    }
  }

  createSlideFromUrl(url) {
    const slide = document.createElement('div');
    slide.className = 'ProductItem__ImageSlide';
    slide.style.scrollSnapAlign = 'start';
    slide.style.scrollSnapStop = 'always';

    const img = document.createElement('img');
    img.className = 'ProductItem__Image';
    img.loading = 'lazy';
    img.alt = '';
    img.src = url.replace('_{width}x.', '_800x.');

    slide.appendChild(img);

    return slide;
  }

  createArrows() {
    const container = this.querySelector('.ProductItem__ImageContainer');
    if (!container) return;

    const prevArrow = document.createElement('button');
    prevArrow.className = 'ProductItem__PrevArrow';
    prevArrow.style.opacity = '0';
    prevArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.86133 1.57653L1.6764 5.4999L5.86133 9.42327L5.14139 10.1912L0.137332 5.4999L5.14139 0.808594L5.86133 1.57653Z" fill="black"/>
      </svg>
    `;
    prevArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(-1);
    };

    const nextArrow = document.createElement('button');
    nextArrow.className = 'ProductItem__NextArrow';
    nextArrow.style.opacity = '0';
    nextArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.138672 9.42347L4.3236 5.5001L0.138671 1.57673L0.85861 0.808798L5.86267 5.5001L0.85861 10.1914L0.138672 9.42347Z" fill="black"/>
      </svg>
    `;
    nextArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(1);
    };

    container.appendChild(prevArrow);
    container.appendChild(nextArrow);
  }

  getRolloverImageIndex() {
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );

    if (alternateImage) {
      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );
      const alternateSrc = alternateImage.getAttribute('src');

      if (alternateSrc) {
        const matchBySrc = carouselImages.find(
          (img) => img.getAttribute('src') === alternateSrc,
        );

        if (matchBySrc) {
          return this.images.indexOf(matchBySrc);
        }
      }

      const alternateSrcset = alternateImage.getAttribute('srcset');

      if (alternateSrcset) {
        const matchBySrcset = carouselImages.find(
          (img) => img.getAttribute('srcset') === alternateSrcset,
        );

        if (matchBySrcset) {
          return this.images.indexOf(matchBySrcset);
        }
      }
    }

    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    return carouselImages.length > 0
      ? this.images.indexOf(carouselImages[0])
      : 0;
  }

  getRolloverImageNumber() {
    const productElement = this.closest('product-item');

    if (productElement) {
      const rolloverData = productElement.getAttribute(
        'data-rollover-image-number',
      );

      if (rolloverData) {
        const number = parseInt(rolloverData, 10);

        return isNaN(number) ? null : number;
      }
    }

    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );

    if (alternateImage) {
      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );
      const alternateSrc = alternateImage.getAttribute('src');

      if (alternateSrc) {
        const matchBySrc = carouselImages.find(
          (img) => img.getAttribute('src') === alternateSrc,
        );

        if (matchBySrc) {
          return carouselImages.indexOf(matchBySrc) + 1;
        }
      }
    }

    return null;
  }

  handleMouseEnter() {
    this.isHovered = true;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.showArrows();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.hideArrows();
  }

  changeImage(direction) {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length === 0) return;

    if (this._activeImageIndex === 0) {
      const rolloverIndex = this.getRolloverImageIndex();
      this._activeImageIndex = rolloverIndex;
    }

    const currentCarouselIndex = carouselImages.findIndex(
      (img) => this.images.indexOf(img) === this._activeImageIndex,
    );

    let newCarouselIndex;

    if (direction > 0) {
      newCarouselIndex = (currentCarouselIndex + 1) % carouselImages.length;
    } else {
      newCarouselIndex = currentCarouselIndex - 1;

      if (newCarouselIndex < 0) {
        newCarouselIndex = carouselImages.length - 1;
      }
    }

    this._activeImageIndex = this.images.indexOf(
      carouselImages[newCarouselIndex],
    );
    this.updateCarousel();
  }

  updateCarousel() {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    carouselImages.forEach((img) => img.classList.remove('active'));

    const activeImage = this.images[this._activeImageIndex];

    if (activeImage?.classList.contains('ProductItem__Image--carousel')) {
      activeImage.classList.add('active');
    }
  }

  showArrows() {
    this.toggleArrows('1');
  }

  hideArrows() {
    this.toggleArrows('0');
  }

  toggleArrows(opacity) {
    if (!this._cachedElements.arrows) {
      this._cachedElements.arrows = this.querySelectorAll(
        '.ProductItem__PrevArrow, .ProductItem__NextArrow',
      );
    }

    this._cachedElements.arrows.forEach(
      (arrow) => (arrow.style.opacity = opacity),
    );
  }

  createProgressBarAfterImageWrapper() {
    if (this.images.length <= 1) return;

    const progressBar = document.createElement('div');
    progressBar.className = 'ProductItem__ProgressBar';

    const progressLine = document.createElement('div');
    progressLine.className = 'ProductItem__ProgressLine';

    const progressFill = document.createElement('div');
    progressFill.className = 'ProductItem__ProgressFill';

    const totalImages =
      this.originalImageCount || this.firstLoopImageCount || this.images.length;
    const segmentWidth = 100 / totalImages;

    progressFill.style.width = `${segmentWidth}%`;
    progressFill.style.transform = 'translateX(0)';

    progressLine.appendChild(progressFill);
    progressBar.appendChild(progressLine);

    const imageWrapper = this.querySelector('.ProductItem__ImageWrapper');

    if (imageWrapper && imageWrapper.parentNode) {
      imageWrapper.parentNode.insertBefore(
        progressBar,
        imageWrapper.nextSibling,
      );
    } else {
      this.appendChild(progressBar);
    }
  }

  handleMobileScroll() {
    if (this._isInitializing) return;

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    const scrollLeft = scrollWrapper.scrollLeft;
    const imageWidth = scrollWrapper.scrollWidth / this.images.length;
    const currentIndex = Math.round(scrollLeft / imageWidth);

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }

    this._rafId = requestAnimationFrame(() => {
      this.updateProgressBarFromScroll(scrollLeft, imageWidth);

      if (currentIndex !== this._activeImageIndex) {
        this._activeImageIndex = currentIndex;
      }

      const totalImages = this.images.length;
      const sequenceLength = this.originalImageCount;

      if (currentIndex > totalImages - sequenceLength) {
        this.addMoreSequences(scrollWrapper);
      }

      this._rafId = null;
    });
  }

  addMoreSequences(scrollWrapper) {
    if (this.addingSequences) return;

    const maxSlides = this.originalImageCount * this.MAX_SLIDES_MULTIPLIER;

    if (!this._cachedElements.slideCount) {
      this._cachedElements.slideCount = scrollWrapper.querySelectorAll(
        '.ProductItem__ImageSlide',
      ).length;
    }

    const currentSlideCount = this._cachedElements.slideCount;

    if (currentSlideCount >= maxSlides) {
      return;
    }

    this.addingSequences = true;

    const secondLoopCount = this.originalImageCount;

    if (
      !this._cachedElements.allSlides ||
      this._cachedElements.allSlides.length !== currentSlideCount
    ) {
      this._cachedElements.allSlides = Array.from(
        scrollWrapper.querySelectorAll('.ProductItem__ImageSlide'),
      );
    }

    const allSlides = this._cachedElements.allSlides;
    const prependedCount = this.INFINITE_LOOPS_COUNT * this.originalImageCount;
    const sequenceStart = prependedCount;

    let patternSlides = allSlides.slice(
      sequenceStart,
      sequenceStart + secondLoopCount,
    );

    if (patternSlides.length < secondLoopCount) {
      patternSlides = allSlides.slice(-secondLoopCount);
    }

    const slidesAfterAdd = currentSlideCount + patternSlides.length;
    if (slidesAfterAdd <= maxSlides) {
      const fragment = document.createDocumentFragment();

      patternSlides.forEach((slide) => {
        const newSlide = slide.cloneNode(true);

        fragment.appendChild(newSlide);
      });
      scrollWrapper.appendChild(fragment);

      this.images = Array.from(scrollWrapper.querySelectorAll('img'));
      this._cachedElements.slideCount = slidesAfterAdd;
      this._cachedElements.allSlides = null;
    }

    setTimeout(() => {
      this.addingSequences = false;
    }, 100);
  }

  updateProgressBar() {
    const progressFill = this.querySelector('.ProductItem__ProgressFill');

    if (!progressFill || !this.originalImageCount || !this.firstLoopImageCount)
      return;

    const adjustedIndex = this._activeImageIndex - (this.prependedSlides || 0);
    const isFirstLoop = this.isInFirstLoop(adjustedIndex);
    const percentage = isFirstLoop
      ? this.calculateFirstLoopProgress(adjustedIndex)
      : this.calculateNaturalOrderProgress(adjustedIndex);

    const imagePosition = (percentage / 100) * this.originalImageCount;
    const translateAmount = imagePosition * 100;

    progressFill.style.transform = `translateX(${translateAmount}%)`;
  }

  isInFirstLoop(adjustedIndex) {
    const roundedIndex = Math.round(adjustedIndex);

    return roundedIndex >= 0 && roundedIndex < this.firstLoopImageCount;
  }

  calculateFirstLoopProgress(adjustedIndex) {
    return (adjustedIndex / this.firstLoopImageCount) * 100;
  }

  calculateNaturalOrderProgress(adjustedIndex) {
    const totalSlides = this.originalImageCount;
    let naturalIndex;
    const rawCalculation = adjustedIndex - this.firstLoopImageCount;

    if (adjustedIndex < 0) {
      naturalIndex = (totalSlides + (adjustedIndex % totalSlides)) % totalSlides;
    } else {
      naturalIndex = rawCalculation % totalSlides;
      if (Math.abs(rawCalculation) < 0.01) {
        naturalIndex = 0;
      }
      if (naturalIndex < 0) naturalIndex += totalSlides;
    }

    return (naturalIndex / totalSlides) * 100;
  }

  updateProgressBarFromScroll(scrollLeft, imageWidth) {
    const progressFill = this.querySelector('.ProductItem__ProgressFill');

    if (!progressFill || !this.originalImageCount || !this.firstLoopImageCount)
      return;

    const currentScrollIndex = scrollLeft / imageWidth;
    const adjustedIndex = currentScrollIndex - (this.prependedSlides || 0);
    const isFirstLoop = this.isInFirstLoop(adjustedIndex);

    const percentage = isFirstLoop
      ? this.calculateFirstLoopProgress(adjustedIndex)
      : this.calculateNaturalOrderProgress(adjustedIndex);

    const imagePosition = (percentage / 100) * this.originalImageCount;
    const translateAmount = imagePosition * 100;

    progressFill.style.transform = `translateX(${translateAmount}%)`;
  }

  setupPageScrollReset() {
    if (this.pageScrollHandler) {
      window.removeEventListener('scroll', this.pageScrollHandler);
    }

    this.pageScrollHandler = () => {
      if (this._scrollTimeout) {
        clearTimeout(this._scrollTimeout);
      }
      this._scrollTimeout = setTimeout(() => this.resetMobileCarousel(), 150);
    };

    window.addEventListener('scroll', this.pageScrollHandler, {
      passive: true,
    });
  }

  resetMobileCarousel() {
    const targetIndex = this.prependedSlides || 0;

    if (!this.isMobile || this._activeImageIndex === targetIndex) return;

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    this.cleanupExcessSlides(scrollWrapper);

    const slideWidth = scrollWrapper.scrollWidth / this.images.length;
    const targetPosition = targetIndex * slideWidth;

    scrollWrapper.scrollTo({ left: targetPosition, behavior: 'smooth' });

    this._activeImageIndex = targetIndex;
    this.updateProgressBar();
  }

  cleanupExcessSlides(scrollWrapper) {
    const allSlides = scrollWrapper.querySelectorAll(
      '.ProductItem__ImageSlide',
    );

    const minKeep = this.originalImageCount * 2;
    const originalStructureLength = (this.prependedSlides || 0) + minKeep;

    if (allSlides.length > originalStructureLength) {
      for (let i = originalStructureLength; i < allSlides.length; i++) {
        allSlides[i].remove();
      }

      this.images = Array.from(scrollWrapper.querySelectorAll('img'));
    }
  }

  updateImages(imageUrls, rolloverImageNumber = null) {
    const wasHovered = this.isHovered;

    let container = this.querySelector('.ProductItem__ImageContainer.AspectRatio');
    if (!container) {
      container = this.querySelector('.AspectRatio');
      if (!container) return;
    }

    const oldScrollWrapper = container.querySelector('.ProductItem__ScrollWrapper');
    if (oldScrollWrapper && this._mobileScrollHandler) {
      oldScrollWrapper.removeEventListener('scroll', this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }

    const validUrls = imageUrls.filter((url) => url);
    if (validUrls.length === 0) return;

    this._activeImageIndex = 0;
    this.isMobile = this.isMobileView();

    if (this.isMobile) {
      const oldImages = Array.from(container.querySelectorAll('img'));
      const oldArrows = Array.from(container.querySelectorAll('.ProductItem__PrevArrow, .ProductItem__NextArrow'));
      const oldWrappers = Array.from(container.querySelectorAll('.ProductItem__ScrollWrapper'));
      const oldProgressBars = Array.from(this.querySelectorAll('.ProductItem__ProgressBar'));

      const mainImage = oldImages.find(img =>
        !img.classList.contains('ProductItem__Image--alternate') &&
        !img.classList.contains('ProductItem__Image--carousel')
      ) || oldImages[0];

      [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => {
        if (el !== mainImage) {
          el.style.opacity = '0';
        }
      });

      if (mainImage) {
        mainImage.style.position = 'absolute';
        mainImage.style.top = '0';
        mainImage.style.left = '0';
        mainImage.style.width = '100%';
        mainImage.style.height = '100%';
        mainImage.style.objectFit = 'cover';
        mainImage.style.zIndex = '10';
        mainImage.style.pointerEvents = 'none';
      }

      const { orderedUrls, rolloverImageNumber: finalRolloverNumber } =
        this.buildUrlSequence(validUrls, rolloverImageNumber);
      const scrollWrapper = this.createScrollWrapperFromUrls(
        validUrls,
        orderedUrls,
      );

      this.rolloverImageNumber = finalRolloverNumber;
      this.setupSequenceCounts(validUrls.length, orderedUrls.length);

      scrollWrapper.style.opacity = '0';

      container.appendChild(scrollWrapper);
      this.images = Array.from(scrollWrapper.querySelectorAll('img'));

      if (this.prependedSlides > 0) {
        scrollWrapper.style.scrollBehavior = 'auto';
        const tempWidth = container.offsetWidth || this.offsetWidth || 300;
        scrollWrapper.scrollLeft = this.prependedSlides * tempWidth;
      }

      this.createProgressBarAfterImageWrapper();

      const showNewCarousel = () => {
        scrollWrapper.style.opacity = '1';

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (mainImage) {
              mainImage.style.display = 'none';
            }

            [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => {
              if (el !== mainImage) {
                el.style.display = 'none';
              }
            });

            setTimeout(() => {
              [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => el.remove());
            }, 100);
          });
        });
      };

      if (this.prependedSlides > 0) {
        this._showCarouselCallback = showNewCarousel;
        this.setInitialScrollPosition(scrollWrapper);
      } else {
        this._isInitializing = true;
        scrollWrapper.scrollLeft = 0;

        const progressFill = this.querySelector('.ProductItem__ProgressFill');

        if (progressFill) {
          progressFill.style.transform = 'translateX(0)';
        }

        setTimeout(() => {
          showNewCarousel();
          this._isInitializing = false;
        }, this.LAYOUT_DELAY_MS);
      }

      this._mobileScrollHandler = this.handleMobileScroll.bind(this);
      scrollWrapper.addEventListener('scroll', this._mobileScrollHandler);
      this.setupPageScrollReset();
    } else {
      const oldImages = Array.from(container.querySelectorAll('img'));
      const oldArrows = Array.from(container.querySelectorAll('.ProductItem__PrevArrow, .ProductItem__NextArrow'));
      const oldWrappers = Array.from(container.querySelectorAll('.ProductItem__ScrollWrapper'));
      const oldProgressBars = Array.from(this.querySelectorAll('.ProductItem__ProgressBar'));

      let alternateIndex = null;

      if (
        rolloverImageNumber &&
        rolloverImageNumber > 1 &&
        rolloverImageNumber <= validUrls.length
      ) {
        alternateIndex = rolloverImageNumber - 1;
      } else if (validUrls.length > 1) {
        alternateIndex = 1;
      }

      const newImages = [];

      if (alternateIndex !== null) {
        const alternateImg = document.createElement('img');
        alternateImg.className = 'ProductItem__Image ProductItem__Image--alternate';
        alternateImg.src = validUrls[alternateIndex].replace('_{width}x.', '_800x.');
        alternateImg.loading = 'eager';
        alternateImg.alt = '';
        container.appendChild(alternateImg);
        newImages.push(alternateImg);
      }

      const mainImg = document.createElement('img');
      mainImg.className = 'ProductItem__Image';
      mainImg.src = validUrls[0].replace('_{width}x.', '_800x.');
      mainImg.loading = 'eager';
      mainImg.alt = '';
      container.appendChild(mainImg);
      newImages.push(mainImg);

      validUrls.forEach((url) => {
        const img = document.createElement('img');
        img.className = 'ProductItem__Image ProductItem__Image--carousel';
        img.src = url.replace('_{width}x.', '_800x.');
        img.loading = 'lazy';
        img.alt = '';
        container.appendChild(img);
        newImages.push(img);
      });

      [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => {
        el.style.display = 'none';
      });

      setTimeout(() => {
        [...oldImages, ...oldArrows, ...oldWrappers, ...oldProgressBars].forEach(el => el.remove());
      }, 50);
    }

    if (!this.isMobile) {
      this.init();

      if (wasHovered) {
        this.isHovered = true;

        const carouselImages = this.images.filter((img) =>
          img.classList.contains('ProductItem__Image--carousel'),
        );

        if (carouselImages.length > 1) {
          this.showArrows();
        }
      }
    }
  }
}

customElements.define('product-item', ProductItem);
